/**
 * ResourceProposalReviewPane — steward's expanded review surface for
 * new-resource proposals (Phase 1.7).
 *
 * Mirrors SuggestionReviewPane (Block 6.C.2 / ADR-021) in mechanic — old
 * value/new value display + Approve/Reject buttons; Reject expands a
 * reviewer-note textarea before commit. Different in DATA: this pane
 * shows the full payload preview (not the field-level diff), since
 * suggested_resources proposes a NEW record.
 *
 * Approve calls applySuggestedResource server-side which transactionally
 * INSERTs into provider_organizations OR provider_individuals + M:N
 * links, then flips status to 'approved' + populates applied_target_id.
 * Cache invalidation:
 *   * ['suggestions-queue'] — the parent's invalidation key (Pending UNION refetch)
 *   * ['admin-review', 'badge-count'] — bell badge sum updates
 *   * ['providers-for-search'] + ['individuals-for-search'] — directory
 *     bulk-load cache; new resource appears in the directory after approve
 */

import { useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTenant } from '@/lib/auth';
import { canApprove } from '@/lib/capabilities';
import type { SuggestedResource, SuggestedResourceTargetType } from '@/lib/types';
import { suggestedResourcesApi } from '@/lib/suggestedResources';
import { suggestionRefusalText } from '@/lib/suggestionRefusals';
import { useSupportContact } from '@/hooks/useSupportContact';
import { formatPhoneDisplay } from '@/lib/format';
import { formatPostalParts, postalPartsOf } from '@/lib/address';
import { SuggestResourceForm } from '@/components/peace-hub-port/SuggestResourceForm';
import { refreshFamily } from '@/lib/refreshFamily';

export interface ResourceProposalReviewPaneProps {
  tenantSlug: string;
  proposal: SuggestedResource;
  invalidationKey: ReadonlyArray<unknown>;
}

export function ResourceProposalReviewPane({
  tenantSlug,
  proposal,
  invalidationKey,
}: ResourceProposalReviewPaneProps): ReactNode {
  const queryClient = useQueryClient();
  const { whoami } = useTenant();
  // Approve/Reject/Edit&approve mirror their endpoints' gate ('approve') —
  // a read-only read_all_suggestions holder (board_observer) sees the
  // payload preview but no disposition buttons (they would always 403).
  const userCanApprove = canApprove(whoami);
  const [rejectMode, setRejectMode] = useState(false);
  const [rejectNote, setRejectNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const contact = useSupportContact();
  // Phase 1.8 — edit-and-approve mode (Q1 disposition). When true, the
  // SuggestResourceForm BottomSheet opens pre-populated with the
  // proposal's payload; steward modifies fields + clicks
  // "Approve & create with edits" which sends final_payload (and
  // final_target_type when steward flipped type) to the approve endpoint.
  const [editMode, setEditMode] = useState(false);

  // The proposal, the note and the list to refresh (every status tab: the
  // family) are read at the press and travel as the variables.
  const approveMutation = useMutation({
    mutationFn: async (v: { id: string; family: readonly unknown[] }) =>
      suggestedResourcesApi.approve(tenantSlug, v.id, { reviewer_note: null }),
    onSuccess: async (_res, v) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: v.family }),
        queryClient.invalidateQueries({ queryKey: ['admin-review', 'badge-count'] }),
        queryClient.invalidateQueries({ queryKey: ['providers-for-search'] }),
        queryClient.invalidateQueries({ queryKey: ['individuals-for-search'] }),
      ]);
    },
    onError: (e: Error) => setError(suggestionRefusalText(e, contact)),
  });

  const rejectMutation = useMutation({
    mutationFn: async (v: { id: string; note: string | null; family: readonly unknown[] }) =>
      suggestedResourcesApi.reject(tenantSlug, v.id, { reviewer_note: v.note }),
    onSuccess: async (_res, v) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: v.family }),
        queryClient.invalidateQueries({ queryKey: ['admin-review', 'badge-count'] }),
      ]);
    },
    onError: (e: Error) => setError(suggestionRefusalText(e, contact)),
  });

  const isPending = approveMutation.isPending || rejectMutation.isPending;

  return (
    <div className="px-3 py-3 border-t border-sage-800/[8%]">
      <PayloadPreview payload={proposal.payload} targetType={proposal.target_type} />

      <div className="text-xs text-ink-soft mt-3 mb-3">
        Proposed by {proposal.suggester_display} ·{' '}
        {formatTimestamp(proposal.created_at)}
      </div>

      {error && (
        <div role="alert" className="text-xs text-red-700 mb-2" data-testid="resource-review-error">
          ⚠ {error}
        </div>
      )}

      {!rejectMode && userCanApprove && (
        <div className="flex gap-2 justify-end">
          <button
            type="button"
            onClick={() => setRejectMode(true)}
            disabled={isPending}
            className="min-h-11 px-3 py-1 text-sm text-warm-700 border border-warm-300 rounded-md hover:bg-warm-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500"
          >
            Reject
          </button>
          {/* Phase 1.8 — Edit-and-approve button. Opens the
              SuggestResourceForm BottomSheet pre-populated with this
              proposal's payload; steward modifies fields + saves via
              "Approve & create with edits." */}
          <button
            type="button"
            onClick={() => setEditMode(true)}
            disabled={isPending}
            className="min-h-11 px-3 py-1 text-sm text-sage-700 border border-sage-300 rounded-md hover:bg-sage-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500"
          >
            Edit & approve
          </button>
          <button
            type="button"
            onClick={() => approveMutation.mutate({ id: proposal.id, family: refreshFamily(invalidationKey) })}
            disabled={isPending}
            className="min-h-11 px-3 py-1 text-sm bg-sage-700 text-white rounded-md hover:bg-sage-800 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500"
          >
            {approveMutation.isPending ? 'Approving…' : 'Approve & create'}
          </button>
        </div>
      )}

      {/* Phase 1.8 — approve-mode SuggestResourceForm. */}
      {editMode && (
        <SuggestResourceForm
          isOpen={editMode}
          onClose={() => setEditMode(false)}
          tenantSlug={tenantSlug}
          submitterDisplayName={null}
          mode="approve"
          existingProposal={proposal}
          onApproveWithEdits={async (
            finalPayload: object,
            finalTargetType: string | null,
          ) => {
            await suggestedResourcesApi.approve(tenantSlug, proposal.id, {
              reviewer_note: null,
              // Cast: form produces a partial OrganizationSubmitPayload OR
              // IndividualSubmitPayload shape; the server re-validates per
              // target_type at the validator boundary (suggestedResources
              // approveSuggestedResource → validateOrganization/IndividualPayload).
              final_payload: finalPayload as unknown as
                | import('@/lib/suggestedResources').OrganizationSubmitPayload
                | import('@/lib/suggestedResources').IndividualSubmitPayload,
              final_target_type: finalTargetType as SuggestedResourceTargetType | null,
            });
            await Promise.all([
              queryClient.invalidateQueries({ queryKey: refreshFamily(invalidationKey) }),
              queryClient.invalidateQueries({ queryKey: ['admin-review', 'badge-count'] }),
              queryClient.invalidateQueries({ queryKey: ['providers-for-search'] }),
              queryClient.invalidateQueries({ queryKey: ['individuals-for-search'] }),
            ]);
            setEditMode(false);
          }}
        />
      )}

      {rejectMode && (
        <div className="border border-warm-300 bg-warm-50 rounded-md p-3 mt-2">
          <label
            className="block text-xs text-ink mb-1"
            htmlFor={`reject-note-${proposal.id}`}
          >
            Reviewer note (optional)
          </label>
          <textarea
            id={`reject-note-${proposal.id}`}
            value={rejectNote}
            onChange={(e) => setRejectNote(e.target.value)}
            rows={3}
            aria-label="Reviewer note"
            disabled={isPending}
            className="w-full min-h-11 px-3 py-2 mb-2 border border-sage-300 rounded-md text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500"
          />
          <div className="flex gap-2 justify-end">
            <button
              type="button"
              onClick={() => {
                setRejectMode(false);
                setRejectNote('');
              }}
              disabled={isPending}
              className="min-h-11 px-3 py-1 text-sm text-ink hover:text-sage-800"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() =>
                rejectMutation.mutate({
                  id: proposal.id,
                  note: rejectNote.trim().length === 0 ? null : rejectNote.trim(),
                  family: refreshFamily(invalidationKey),
                })
              }
              disabled={isPending}
              className="min-h-11 px-3 py-1 text-sm bg-warm-500 text-white rounded-md hover:bg-warm-600 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500"
            >
              {rejectMutation.isPending ? 'Rejecting…' : 'Confirm reject'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Payload preview — compact summary of what the new resource will be.
 * Renders the key fields per target_type without exhaustive enumeration
 * (steward scans for review). Defensive against shape variance.
 */
function PayloadPreview({
  payload,
  targetType,
}: {
  payload: Record<string, unknown>;
  targetType: SuggestedResource['target_type'];
}): ReactNode {
  if (targetType === 'organization') {
    return (
      <div className="text-sm space-y-1">
        <PreviewLine label="Name" value={asString(payload['name'])} />
        <PreviewLine label="Category" value={asString(payload['category_slug'])} />
        <PreviewLine label="Type" value={asString(payload['kind'])} />
        <PreviewLine label="Phone" value={asString(payload['phone'])} />
        <PreviewLine label="Address" value={asAddress(payload['address'])} />
        <PreviewLine label="Website" value={asString(payload['website'])} />
        <PreviewLine
          label="Subcategories"
          value={asStringArray(payload['subcategories'])}
        />
        <PreviewLine
          label="Specialties"
          value={asStringArray(payload['specialties'])}
        />
        <PreviewLine label="Counties" value={asStringArray(payload['counties'])} />
        <PreviewLine label="Medicaid" value={asString(payload['medicaid'])} />
        <PreviewLine
          label="Dementia unit"
          value={
            payload['dementia_unit'] === true
              ? 'Yes'
              : payload['dementia_unit'] === false
                ? 'No'
                : null
          }
        />
        {/* F2 suggest-fields — facet selections that approval now carries into
            the provider record (payment_types column + highlight / service-model
            assignments). */}
        <PreviewLine label="Payment types" value={asStringArray(payload['payment_types'])} />
        <PreviewLine label="Gender restrictions" value={asString(payload['gender_restrictions'])} />
        <PreviewLine label="Delivery methods" value={asStringArray(payload['service_models'])} />
        {/* Phase 1.7.4 — multi-add contacts. Prefer the canonical
            `contacts[]` array (per-entry existing_id / inline); fall
            back to legacy singular fields for pre-1.7.4 pending
            proposals. */}
        <ContactsPreview payload={payload} />
        <PreviewLine label="Notes" value={asString(payload['notes'])} multiline />
      </div>
    );
  }
  return (
    <div className="text-sm space-y-1">
      <PreviewLine label="Full name" value={asString(payload['full_name'])} />
      <PreviewLine label="Title" value={asString(payload['title'])} />
      <PreviewLine label="Email" value={asString(payload['email'])} />
      <PreviewLine label="Phone" value={asString(payload['phone'])} />
      <PreviewLine label="Specialties" value={asString(payload['specialties'])} />
      <PreviewLine label="Notes" value={asString(payload['notes'])} multiline />
      <PreviewLine
        label="Affiliations"
        value={asAffiliationSummary(payload['affiliations'])}
      />
    </div>
  );
}

/**
 * Phase 1.7.4 — render contact entries from the canonical `contacts[]`
 * array if present, otherwise from the legacy singular fields. Each
 * entry surfaces as its own line so the steward can scan multi-add
 * proposals.
 */
function ContactsPreview({ payload }: { payload: Record<string, unknown> }): ReactNode {
  const contactsRaw = payload['contacts'];
  if (Array.isArray(contactsRaw) && contactsRaw.length > 0) {
    return (
      <>
        {contactsRaw.map((c, i) => {
          if (typeof c !== 'object' || c === null) return null;
          const entry = c as Record<string, unknown>;
          if (typeof entry['existing_id'] === 'string') {
            return (
              <PreviewLine
                key={`existing-${i}`}
                label={i === 0 ? 'Contact (existing)' : ''}
                value={entry['existing_id']}
              />
            );
          }
          if (typeof entry['inline'] === 'object' && entry['inline'] !== null) {
            return (
              <PreviewLine
                key={`inline-${i}`}
                label={i === 0 ? 'Contact (new)' : ''}
                value={asContactInlineSummary(entry['inline'])}
              />
            );
          }
          return null;
        })}
      </>
    );
  }
  // Legacy singular shape.
  return (
    <>
      <PreviewLine
        label="Contact (existing)"
        value={asString(payload['contact_individual_id'])}
      />
      <PreviewLine
        label="Contact (new)"
        value={asContactInlineSummary(payload['contact_individual_inline'])}
      />
    </>
  );
}

function PreviewLine({
  label,
  value,
  multiline,
}: {
  label: string;
  value: string | null;
  multiline?: boolean;
}): ReactNode {
  if (!value) return null;
  return (
    <div className="flex gap-2">
      <span className="text-xs font-semibold text-ink-soft min-w-[110px]">{label}</span>
      <span className={multiline ? 'whitespace-pre-wrap text-ink' : 'text-ink'}>
        {value}
      </span>
    </div>
  );
}

function asString(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  const trimmed = v.trim();
  return trimmed.length === 0 ? null : trimmed;
}

/** An address proposed as its parts reads as its formatted text; a
 *  legacy string as it is. */
function asAddress(v: unknown): string | null {
  const parts = postalPartsOf(v);
  return parts !== null ? formatPostalParts(parts) : asString(v);
}

function asStringArray(v: unknown): string | null {
  if (!Array.isArray(v) || v.length === 0) return null;
  return v.filter((x) => typeof x === 'string').join(', ');
}

function asAffiliationSummary(v: unknown): string | null {
  if (!Array.isArray(v) || v.length === 0) return null;
  // Phase 1.7.4 — count existing vs inline-create entries separately
  // so the steward sees "3 orgs (1 new)" rather than just "3 orgs".
  let existing = 0;
  let inline = 0;
  for (const entry of v) {
    if (typeof entry !== 'object' || entry === null) continue;
    const e = entry as Record<string, unknown>;
    if (typeof e['provider_organization_id'] === 'string') {
      existing += 1;
    } else if (typeof e['inline'] === 'object' && e['inline'] !== null) {
      inline += 1;
    }
  }
  const total = existing + inline;
  if (total === 0) return null;
  if (inline === 0) return `${total} org${total === 1 ? '' : 's'}`;
  return `${total} org${total === 1 ? '' : 's'} (${inline} new)`;
}

/**
 * Render an inline-create contact-individual payload as a single-line
 * summary for the review pane. The full inline payload is a nested
 * IndividualPayload; we surface name + title + business_phone +
 * cell_phone for steward scan.
 */
function asContactInlineSummary(v: unknown): string | null {
  if (typeof v !== 'object' || v === null || Array.isArray(v)) return null;
  const inline = v as Record<string, unknown>;
  const fullName = typeof inline['full_name'] === 'string' ? inline['full_name'] : null;
  if (!fullName) return null;
  const parts: string[] = [fullName];
  if (typeof inline['title'] === 'string' && inline['title']) {
    parts.push(`(${inline['title']})`);
  }
  const phone =
    (typeof inline['business_phone'] === 'string' && inline['business_phone']) ||
    (typeof inline['cell_phone'] === 'string' && inline['cell_phone']) ||
    (typeof inline['phone'] === 'string' && inline['phone']);
  if (phone) parts.push(`· ${formatPhoneDisplay(phone)}`);
  return parts.join(' ');
}

function formatTimestamp(iso: string | null): string {
  if (iso === null) return '–';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const ms = Date.now() - d.getTime();
  const min = Math.floor(ms / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hr ago`;
  const days = Math.floor(hr / 24);
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}
